import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

import { Box, Button, Dialog, Fab, Modal as ModalMui, Stack, styled } from "@mui/material";
import { ArrowsOutSimpleIcon } from "@phosphor-icons/react/dist/csr/ArrowsOutSimple";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { BACKFILLS_LIST_QUERY } from "../../graphql";
import type {
  ExportDeclarationQuery,
  ExportDeclarationQueryVariables,
  ExportDeclarationsQuery,
  ExportDeclarationsQueryVariables,
} from "../../graphql/types";
import {
  Backfill,
  ExportDeclarationDocument,
  ExportDeclarationsDocument,
  LaunchBackfillDocument,
  LaunchBackfillMutation,
  LaunchBackfillMutationVariables,
} from "../../graphql/types";
import { Steps, useBackfillStore } from "../../store/backfillsStore";
import { DataLoader, SuccessContainer } from "../DataLoader";
import { StatefulButton } from "../ui/muiButtons";
import { StaticDiagram } from "./Diagram";
import { DynamicDiagram } from "./DynamicDiagram";
import { AditionalSelections } from "./step/AditionalSelections";
import { BackfillReview } from "./step/BackfillReview";
import { SelectUpload } from "./step/SelectUpload";
const STEPS = [
  {
    title: "Choose a date range and an upload.",
  },
  {
    title: "Make additional selections",
  },
  {
    title: "Review Backfill",
  },
];

const Modal = styled(ModalMui)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  height: "95%",
});

const BoxModalContainer = styled(Box)(({ theme }) => ({
  height: "95vh",
  overflowY: "auto",
  width: "100%",
  background: theme.palette.surfaceLight10.main,
  alignItems: "center",
}));

const BoxDialogContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  padding: theme.spacing(4, 3, 10, 3),
  minHeight: "700px",
  height: "100%",
  overflowY: "auto",
}));

enum ErrorType {
  empty = "",
  conflictingBackfillsError = "ConflictingBackfillsError",
  otherError = "OtherError",
}

const StackBottomButtons = styled(Stack)({
  position: "absolute",
  bottom: "12px",
  right: "12px",
});

const CloseIconContainer = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: 0,
  bottom: 0,
  zIndex: theme.zIndex["fab"],
  padding: theme.spacing(1),
}));

const FullScreenButton = ({
  staticDiagram = false,
  setShowFullScreen,
  showFullScreen,
}: {
  staticDiagram?: boolean;
  setShowFullScreen: Dispatch<
    SetStateAction<{
      dynamicDiagram: boolean;
      staticDiagram: boolean;
    }>
  >;
  showFullScreen: {
    dynamicDiagram: boolean;
    staticDiagram: boolean;
  };
}) => {
  return (
    <Stack direction="row" className="hide" sx={{ width: "100%", justifyContent: "flex-end", pr: 1, pt: 1 }}>
      <Fab
        size="small"
        onClick={() =>
          setShowFullScreen({
            ...showFullScreen,
            ...(staticDiagram ? { staticDiagram: true } : { dynamicDiagram: true }),
          })
        }
      >
        <ArrowsOutSimpleIcon />
      </Fab>
    </Stack>
  );
};

interface IDynamicDiagramWrapper {
  fullWidth?: boolean;

  setShowFullScreen: Dispatch<
    SetStateAction<{
      dynamicDiagram: boolean;
      staticDiagram: boolean;
    }>
  >;
  showFullScreen: {
    dynamicDiagram: boolean;
    staticDiagram: boolean;
  };
  showIcons?: boolean;
}

const DynamicDiagramWrapper: FC<IDynamicDiagramWrapper> = ({
  fullWidth = false,
  setShowFullScreen,
  showFullScreen,
  showIcons,
}) => {
  return (
    <DynamicDiagram
      fullWidth={fullWidth}
      fullScreenButton={<FullScreenButton setShowFullScreen={setShowFullScreen} showFullScreen={showFullScreen} />}
      showIcons={showIcons || false}
    />
  );
};

interface IBackfillBottomButtons {
  handleBack: () => void;
  handlePublish: () => void;
  handleNext: () => void;
  loading: boolean;
  step: Steps;
  disabled: boolean;
}

const BottomButtons: FC<IBackfillBottomButtons> = ({
  handleBack,
  handlePublish,
  handleNext,
  loading,
  step,
  disabled,
}) => {
  return (
    <StackBottomButtons direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
      {step > 0 ? (
        <Button onClick={handleBack} disabled={step === 0} data-cy="back-button" variant="outlined">
          Back
        </Button>
      ) : null}
      <StatefulButton
        onClick={step === 2 ? handlePublish : handleNext}
        disabled={disabled}
        data-cy="next-button"
        loading={loading}
      >
        {step === 2 ? "Launch" : "Next"}
      </StatefulButton>
    </StackBottomButtons>
  );
};

const BackfillModal = () => {
  const showModal = useBackfillStore((state) => state.showModal);
  const step = useBackfillStore((state) => state.step);
  const uploadName = useBackfillStore((state) => state.uploadName);
  const backfillNameRef = useRef(useBackfillStore.getState().backfillName);
  const backfillDescriptionRef = useRef(useBackfillStore.getState().backfillDescription);
  const isLoading = useBackfillStore((state) => state.isLoading);
  const dateRef = useRef(useBackfillStore.getState().date);
  const currentSubgraphRef = useRef(useBackfillStore.getState().currentSubgraph);

  const goStepBack = useBackfillStore((state) => state.goStepBack);
  const nextStep = useBackfillStore((state) => state.nextStep);
  const toggleModalOpen = useBackfillStore((state) => state.toggleModalOpen);
  const doneLoading = useBackfillStore((state) => state.doneLoading);
  const setErrorLaunching = useBackfillStore((state) => state.setErrorLaunching);
  const [showFullScreen, setShowFullScreen] = useState({
    dynamicDiagram: false,
    staticDiagram: false,
  });

  const queryUploadNameRef = useRef<string>("");

  const resetCurrentSubgraph = useBackfillStore((state) => state.resetCurrentSubgraph);
  const updateGraph = useBackfillStore((state) => state.updateGraph);
  const setSubgraph = useBackfillStore((state) => state.setSubGraph);
  const errorLaunching = useBackfillStore((state) => state.errorType !== ErrorType.empty);
  useEffect(() => {
    useBackfillStore.subscribe((state) => (dateRef.current = state.date));
    useBackfillStore.subscribe((state) => (backfillDescriptionRef.current = state.backfillDescription));
    useBackfillStore.subscribe((state) => (backfillNameRef.current = state.backfillName));
    useBackfillStore.subscribe((state) => (currentSubgraphRef.current = state.currentSubgraph));
  }, []);

  const [loadQuery, exportDeclarationsQueryState] = useLazyQuery<
    ExportDeclarationQuery,
    ExportDeclarationQueryVariables
  >(ExportDeclarationDocument, {
    errorPolicy: "all",
    fetchPolicy: "network-only",
  });

  const isSingleSelectionGraph = useMemo(() => {
    const data = exportDeclarationsQueryState.data;
    return Boolean(
      data?.export_declaration?.subgraph && data.export_declaration.subgraph.upload_declarations.length < 2
    );
  }, [exportDeclarationsQueryState.data]);

  useEffect(() => {
    if (!exportDeclarationsQueryState.called || exportDeclarationsQueryState.loading) return;

    doneLoading();

    const data = exportDeclarationsQueryState.data;
    if (!data) return;

    const download_declarations = data.export_declaration?.subgraph?.download_declarations ?? [];
    const upload_declarations = data.export_declaration?.subgraph?.upload_declarations ?? [];

    const import_names: string[] = [];
    const export_names: string[] = [];
    const links: { [key: string]: string[] } = {};
    download_declarations.forEach((elem) => {
      if (!elem) {
        return;
      }

      let name = "";
      const newExports: string[] = [];
      //if it is a bundle, i have to go through all of the tables
      if (elem.bundle_name) {
        name = elem.bundle_name;
        elem.tables.forEach((tableElem) => {
          const exports = tableElem?.exports ?? [];
          exports.forEach((element) => {
            newExports.push(element?.name ?? "");
          });
        });
      } else {
        name = elem.tables[0]?.name ?? "";
        elem.tables[0]?.exports.forEach((element) => {
          newExports.push(element?.name ?? "");
        });
      }
      import_names.push(name);
      links[`${name}`] = newExports;
    });
    //add all the exports
    upload_declarations.forEach((elem) => {
      export_names.push(elem?.name ?? "");
    });
    setSubgraph({
      import_names,
      export_names,
      links,
    });
    //we have to add this upload to preload the following graph
    updateGraph(queryUploadNameRef.current);

    const singleSelectionGraph =
      data?.export_declaration?.subgraph && data.export_declaration.subgraph.upload_declarations.length < 2;
    if (singleSelectionGraph) {
      nextStep(false);
    }
  }, [
    exportDeclarationsQueryState.called,
    exportDeclarationsQueryState.loading,
    exportDeclarationsQueryState.data,
    doneLoading,
    nextStep,
    setSubgraph,
    updateGraph,
  ]);

  const onLaunchBackfillComplete = (response: LaunchBackfillMutation) => {
    const data = response.launch_backfill;
    if (data == null) {
      setErrorLaunching(ErrorType.otherError);
    } else if (data.__typename === "Backfill") {
      toast.success("Backfill launched");
      toggleModalOpen();
    } else if (data.__typename === ErrorType.conflictingBackfillsError) {
      setErrorLaunching(ErrorType.conflictingBackfillsError, data.conflicting_backfills as Backfill[]);
    }
  };

  const [launchBackfill, launchBackfillState] = useMutation<LaunchBackfillMutation, LaunchBackfillMutationVariables>(
    LaunchBackfillDocument,
    {
      refetchQueries: () => [BACKFILLS_LIST_QUERY],
      awaitRefetchQueries: false,
      onCompleted: (data) => {
        onLaunchBackfillComplete(data);
      },
      onError: () => {
        setErrorLaunching(ErrorType.otherError);
      },
    }
  );

  const handleBack = () => {
    if (errorLaunching) {
      setErrorLaunching(ErrorType.empty);
    }
    if (step === 2 && isSingleSelectionGraph) {
      goStepBack(2);
      resetCurrentSubgraph();
    } else if (step === 2) {
      goStepBack(1);
    } else {
      // step must be 1
      goStepBack(1);
      resetCurrentSubgraph();
    }
  };

  const handlePublish = () => {
    const startDateUtc = format(dateRef.current.start, "yyyy-MM-dd") + "T00:00:00.000Z";
    const endDateUtc = format(dateRef.current.end, "yyyy-MM-dd") + "T23:59:59.000Z";
    const payload = {
      variables: {
        upload_names: currentSubgraphRef.current.export_names,
        title: backfillNameRef.current,
        description: backfillDescriptionRef.current,
        start_datetime: startDateUtc,
        end_datetime: endDateUtc,
      },
    };
    launchBackfill(payload);
  };
  const handleNext = () => {
    if (step === 0) {
      //if we are going to the second step, we must reset the current subgraph
      resetCurrentSubgraph();
      queryUploadNameRef.current = uploadName;
      loadQuery({ variables: { name: uploadName } });
    }
    nextStep(step === 0);
  };

  const { data: exportListData, loading: exportListDataLoading } = useQuery<
    ExportDeclarationsQuery,
    ExportDeclarationsQueryVariables
  >(ExportDeclarationsDocument, {
    variables: { first: 1000 },
    fetchPolicy: "network-only",
  });

  const isCurrentSubgraphEmpty = useBackfillStore((state) => Object.keys(state.currentSubgraph.links).length === 0);
  const isEmptyBackfillName = useBackfillStore(
    (state) => state.backfillName !== null && state.backfillName.trim() === ""
  );
  const isEmptyUploadName = useBackfillStore((state) => state.uploadName === "");

  const isDateInvalid = useBackfillStore((state) => {
    const { start, end } = state.date;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      !start || !end || isNaN(start.getTime()) || isNaN(end.getTime()) || start > end || start > today || end > today
    );
  });

  const isDisabled = () => {
    switch (step) {
      case 0:
        return isEmptyUploadName || isDateInvalid;
      case 1:
        return isCurrentSubgraphEmpty;
      case 2:
        return errorLaunching || isEmptyBackfillName;
      default:
        return false;
    }
  };

  const isModalLoading = isLoading || launchBackfillState.loading || exportDeclarationsQueryState.loading;
  const completedPercentage = ((step + 1) * 100) / STEPS.length;
  const stepTitle = STEPS[step].title;

  return (
    <>
      <Dialog open={showModal} onClose={() => toggleModalOpen()} maxWidth="md" fullWidth data-cy="backfill-modal">
        <BoxDialogContainer>
          <DataLoader loading={isModalLoading} success={!isModalLoading}>
            <SuccessContainer>
              <Stack
                direction="column"
                spacing={3}
                sx={{ alignItems: "center", justifyContent: "flex-start", flex: 1, height: "90%" }}
              >
                {step === 0 && (
                  <SelectUpload
                    title={stepTitle}
                    exportListData={exportListData}
                    completedPercentage={completedPercentage}
                    exportsLoading={exportListDataLoading}
                  />
                )}
                {step === 1 && (
                  <AditionalSelections title={stepTitle} completedPercentage={completedPercentage}>
                    <DynamicDiagramWrapper setShowFullScreen={setShowFullScreen} showFullScreen={showFullScreen} />
                  </AditionalSelections>
                )}
                {step === 2 && (
                  <BackfillReview title={stepTitle} completedPercentage={completedPercentage}>
                    <StaticDiagram
                      fullScreenButton={
                        <FullScreenButton
                          staticDiagram
                          setShowFullScreen={setShowFullScreen}
                          showFullScreen={showFullScreen}
                        />
                      }
                    />
                  </BackfillReview>
                )}
                <BottomButtons
                  handleBack={handleBack}
                  handleNext={handleNext}
                  loading={launchBackfillState.loading}
                  handlePublish={handlePublish}
                  step={step}
                  disabled={isDisabled()}
                />
              </Stack>
            </SuccessContainer>
          </DataLoader>
        </BoxDialogContainer>
      </Dialog>
      <Modal open={showFullScreen.dynamicDiagram || showFullScreen.staticDiagram}>
        <BoxModalContainer>
          <CloseIconContainer direction="row" sx={{ justifyContent: "flex-end", width: "100%" }}>
            <Fab
              size="small"
              onClick={() =>
                setShowFullScreen({
                  dynamicDiagram: false,
                  staticDiagram: false,
                })
              }
            >
              <XIcon />
            </Fab>
          </CloseIconContainer>
          {showFullScreen.dynamicDiagram ? (
            <DynamicDiagramWrapper
              fullWidth
              setShowFullScreen={setShowFullScreen}
              showFullScreen={showFullScreen}
              showIcons={true}
            />
          ) : (
            <StaticDiagram
              fullWidth
              fullScreenButton={
                <FullScreenButton staticDiagram setShowFullScreen={setShowFullScreen} showFullScreen={showFullScreen} />
              }
              showIcons={true}
            />
          )}
        </BoxModalContainer>
      </Modal>
    </>
  );
};

export { BackfillModal };
